import React, { useState, useEffect } from 'react';
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import PortalContainer from "../../portal/PortalContainerHook";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import { getCategoryTemplate } from "../../actions/categoryTemplateActions";
import { getGroupTemplate } from "../../actions/groupTemplateActions";
import { getQuestionTemplate } from "../../actions/questionTemplateActions";
import * as TEMPLATE from "../../constants/templateConstants";
import * as defaultGraph from "../../constants/defaultGraph"
import * as portalShared from "../../portal/shared/metadataConstantsAndUtilities"


const ArgoPortalPreviewHook = (props) => {
    const {
        createMode,
        templateType,
        template,
        answerOptionTemplates,
        hidePortalPreview,
        id,
        templateGraph
    } = props;

    const [state, setState] = useState({
        componentMounted: false,
        portalCount: -1,
        allTemplatesLoaded: false,
        questionnaireTemplateGraph: null
    });

    const dispatch = useDispatch();

    // Redux state
    const categoryTemplates = useSelector(state => state.categoryTemplate);
    const groupTemplates = useSelector(state => state.groupTemplate);
    const questionTemplates = useSelector(state => state.questionTemplate);
    const portalCount = useSelector(state => state.portal.portalCount);

    // Signal component mount
    useEffect(() => {

        setState(prevState => ({
            ...prevState,
            componentMounted: true
        }));
    }, []);

    // Handle template loading and building questionnaire graph for preview
    useEffect(() => {
        //const loadTemplatesAndBuildGraph = () => {
        const allTemplatesLoaded = (state.portalCount === portalCount);

        if ((createMode || (template?.templateKey !== "")) && (!allTemplatesLoaded)) {
            let categoryKeys = [];
            let groupKeys = [];
            let questionKeys = [];
            let showGroupKeys = [];
            let allDependenciesLoaded = false;

            switch (templateType) {
                case TEMPLATE.TYPE.QUESTIONNAIRE_GRAPH:
                case TEMPLATE.TYPE.CATEGORY_GRAPH:
                case TEMPLATE.TYPE.GROUP_GRAPH:
                    // If there is an ID then the graph is loaded
                    if (templateGraph.hasOwnProperty("id")) {
                        allDependenciesLoaded = true;
                    }
                    break;
                case TEMPLATE.TYPE.QUESTIONNAIRE:
                    categoryKeys = (template && template.categoryKeys) || [];
                    allDependenciesLoaded = checkForAllTemplatesLoaded(categoryKeys, categoryTemplates, key => dispatch(getCategoryTemplate(key)));
                    if (allDependenciesLoaded && (categoryKeys.length > 0)) {

                        showGroupKeys = template.showGroupKeys || [];
                        allDependenciesLoaded = checkForAllTemplatesLoaded(showGroupKeys, groupTemplates, key => dispatch(getGroupTemplate(key)));

                        groupKeys = getAllKeys(categoryTemplates, "groupKeys"); // Combine all group keys from all categories
                        allDependenciesLoaded = checkForAllTemplatesLoaded(groupKeys, groupTemplates, key => dispatch(getGroupTemplate(key)));
                        if (allDependenciesLoaded && (groupKeys.length > 0)) {
                            questionKeys = getAllKeys(groupTemplates, "questionKeys"); // Combine all question keys from all groups
                            allDependenciesLoaded = checkForAllTemplatesLoaded(questionKeys, questionTemplates, key => dispatch(getQuestionTemplate(key)));
                        }
                    }
                    break;
                case TEMPLATE.TYPE.CATEGORY:
                    showGroupKeys = (template && template.showGroupKeys) || [];
                    allDependenciesLoaded = checkForAllTemplatesLoaded(showGroupKeys, groupTemplates, key => dispatch(getGroupTemplate(key)));

                    groupKeys = (template && template.groupKeys) || [];
                    allDependenciesLoaded = checkForAllTemplatesLoaded(groupKeys, groupTemplates, key => dispatch(getGroupTemplate(key)));
                    if (allDependenciesLoaded && (groupKeys.length > 0)) {
                        questionKeys = getAllKeys(groupTemplates, "questionKeys"); // Combine all question keys from all groups
                        allDependenciesLoaded = checkForAllTemplatesLoaded(questionKeys, questionTemplates, key => dispatch(getQuestionTemplate(key)));
                    }
                    break;
                case TEMPLATE.TYPE.GROUP:
                    showGroupKeys = (template && template.showGroupKeys) || [];
                    allDependenciesLoaded = checkForAllTemplatesLoaded(showGroupKeys, groupTemplates, key => dispatch(getGroupTemplate(key)));

                    if (allDependenciesLoaded) {

                        // Get the questions assigned to the group
                        questionKeys = (template && template.questionKeys) || [];
                        allDependenciesLoaded = checkForAllTemplatesLoaded(questionKeys, questionTemplates, key => dispatch(getQuestionTemplate(key)));

                        if (allDependenciesLoaded) {
                            // Get any question from show groups from assigned questions
                            questionKeys = getAllKeys(groupTemplates, "questionKeys"); // Combine all question keys from all groups
                            allDependenciesLoaded = checkForAllTemplatesLoaded(questionKeys, questionTemplates, key => dispatch(getQuestionTemplate(key)));
                        }

                    }
                    break;
                case TEMPLATE.TYPE.QUESTION:
                    showGroupKeys = (template && template.showGroupKeys) || [];
                    allDependenciesLoaded = checkForAllTemplatesLoaded(showGroupKeys, groupTemplates, key => dispatch(getGroupTemplate(key)));

                    if (allDependenciesLoaded && (showGroupKeys.length > 0)) {
                        questionKeys = getAllKeys(groupTemplates, "questionKeys"); // Combine all question keys from all groups
                        allDependenciesLoaded = checkForAllTemplatesLoaded(questionKeys, questionTemplates, key => dispatch(getQuestionTemplate(key)));
                    }
                    break;
                // no default
            }

            if (allDependenciesLoaded) {
                let questionnaireTemplateGraph = null;

                if (templateType === TEMPLATE.TYPE.QUESTIONNAIRE_GRAPH) {
                    questionnaireTemplateGraph = adjustQuestionnaireTemplateGraph(templateGraph);
                }
                else if (templateType === TEMPLATE.TYPE.CATEGORY_GRAPH) {
                    questionnaireTemplateGraph = portalShared.cloneObj(defaultGraph.questionnaireGraph);
                    questionnaireTemplateGraph.questionCategoryTemplates.shift(); // Remove default category in array
                    questionnaireTemplateGraph.questionCategoryTemplates.push(templateGraph);
                    questionnaireTemplateGraph = adjustQuestionnaireTemplateGraph(questionnaireTemplateGraph);
                }
                else if (templateType === TEMPLATE.TYPE.GROUP_GRAPH) {

                    questionnaireTemplateGraph = portalShared.cloneObj(defaultGraph.questionnaireGraph);
                    questionnaireTemplateGraph.questionCategoryTemplates[0].groupTemplates.shift(); // Remove default group in array
                    questionnaireTemplateGraph.questionCategoryTemplates[0].groupTemplates.push(templateGraph);

                    // Add show groups
                    if (templateGraph.showGroups.length) {
                        for (let i=0; i < templateGraph.showGroups.length; i++) {
                            questionnaireTemplateGraph.questionCategoryTemplates[0].groupTemplates.push(templateGraph.showGroups[i]);
                        }
                    }

                    questionnaireTemplateGraph = adjustQuestionnaireTemplateGraph(questionnaireTemplateGraph);
                } else {

                    if (template) {
                        questionnaireTemplateGraph = buildQuestionnaireGraphTemplateForPreview({
                            template,
                            categoryTemplates,
                            groupTemplates,
                            questionTemplates,
                            answerOptionTemplates,
                            templateType
                        }, categoryKeys, groupKeys, categoryKeys);
                    }
                }

                setState(prevState => ({
                    ...prevState,
                    allTemplatesLoaded: true,
                    portalCount: portalCount,
                    questionnaireTemplateGraph: questionnaireTemplateGraph
                }));
            }
        }
        //};

        //loadTemplatesAndBuildGraph(); AI Created a infinite loop
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [template, portalCount, templateType, createMode, categoryTemplates, groupTemplates, questionTemplates, answerOptionTemplates, templateGraph, dispatch]);

    // Helper functions
    const checkForAllTemplatesLoaded = (keyArray, templates, getTemplate) => {
        if (!keyArray || !templates) return true;

        let allTemplatesLoaded = true;
        for (let i = 0; i < keyArray.length; i++) {
            if (!templates.hasOwnProperty(keyArray[i])) {
                getTemplate(keyArray[i]);
                allTemplatesLoaded = false;
                break;
            }
        }
        return allTemplatesLoaded;
    };

    const getAllKeys = (templates, keyProperty) => {
        if (!templates) return [];

        let keysArray = [];
        Object.keys(templates).forEach(key => {
            for (let i = 0; i < (templates[key][keyProperty] || []).length; i++) {
                keysArray.push(templates[key][keyProperty][i]);
            }
        });
        return keysArray;
    };

    const getTemplatesArray = (keysArray, templates) => {
        if (!keysArray || !templates) return [];

        let tempTemplatesArray = [];
        for (let i = 0; i < keysArray.length; i++) {
            if (templates[keysArray[i]]) {
                tempTemplatesArray.push(templates[keysArray[i]]);
            }
        }
        return tempTemplatesArray;
    };

    const adjustQuestionnaireTemplateGraph = (questionnaireTemplateGraph) => {
        if (!questionnaireTemplateGraph) return null;

        // NOTE: This adds group answers to each of the questions
        // And for answers adds a selected property
        let tempQnr = portalShared.cloneObj(questionnaireTemplateGraph);
        tempQnr["questions"] = undefined; // This is used in instances checks to determine if instance or template mode

        for (let c = 0; c < (tempQnr.questionCategoryTemplates || []).length; c++) {
            let category = tempQnr.questionCategoryTemplates[c];

            for (let g = 0; g < (category.groupTemplates || []).length; g++) {
                let group = category.groupTemplates[g];

                if (group.questionGroupType === "LIKERT") {
                    for (let i = 0; i < (group.questionTemplates || []).length; i++) {
                        let question = group.questionTemplates[i];
                        question.answerOptionTemplates = group.answerOptionTemplates; // Apply answers to LIKERT question

                        for (let a = 0; a < (question.answerOptionTemplates || []).length; a++) {
                            question.answerOptionTemplates[a].selected = false; // add selected property to object
                            question.answerOptionTemplates[a].key = question.answerOptionTemplates[a].templateKey; // add key property to object
                        }
                    }
                } else {
                    for (let q = 0; q < (group.questionTemplates || []).length; q++) {
                        let question = group.questionTemplates[q];

                        if ((question.answerOptionTemplates || []).length > 0) {
                            for (let a = 0; a < question.answerOptionTemplates.length; a++) {
                                question.answerOptionTemplates[a].selected = false; // add selected property to object
                                question.answerOptionTemplates[a].key = question.answerOptionTemplates[a].templateKey; // add key property to object
                            }
                        } else {
                            question.answerText = "";
                        }
                    }
                }
            }
        }

        return tempQnr;
    };

    const buildQuestionnaireGraphTemplateForPreview = (props) => {
        let templateClone = portalShared.cloneObj(props.template); // Clone template object
        let categoryTemplatesClone = portalShared.cloneObj(props.categoryTemplates);
        let groupTemplatesClone = portalShared.cloneObj(props.groupTemplates);
        let questionTemplatesClone = portalShared.cloneObj(props.questionTemplates);

        let defaultQuestionnaireTemplate;

        if (props.templateType === TEMPLATE.TYPE.QUESTIONNAIRE) {
            defaultQuestionnaireTemplate = templateClone;
        } else {
            defaultQuestionnaireTemplate = {
                "id": 1,
                "templateKey": "questionnaireKey1",
                "name": "",
                "description": "",
                "metadata": {},
                "questionnaireType": "QUESTIONNAIRE_COMPLEX",
                "questionTemplates": [],
                "questionCategoryTemplates": [],
            };
        }

        let defaultCategoryTemplate = {
            "id": 11,
            "templateKey": "categoryKey11",
            "name": "",
            "description": "",
            "metadata": {[portalShared.CUSTOM_DDO_NAME]: "hide"},
            "groupTemplates": [],
            "showGroupKeys": []
        };

        let defaultGroupTemplate = {
            "id": 111,
            "templateKey": "groupKey111",
            "name": "",
            "description": "",
            "questionGroupType": "DEFAULT",
            "visible": true,
            "questionTemplates": [],
            "answerOptionTemplates": [],
            "metadata": {[portalShared.CUSTOM_DDO_NAME]: "hide"},
            "showGroupKeys": [],
        };

        if (props.template !== null) {
            if (props.templateType === TEMPLATE.TYPE.QUESTIONNAIRE) {
                if (templateClone.id === undefined) {
                    defaultQuestionnaireTemplate.id = 1;
                    defaultQuestionnaireTemplate.templateKey = "questionnaireKey1";
                }

                defaultQuestionnaireTemplate.questionCategoryTemplates = getTemplatesArray(templateClone.categoryKeys, categoryTemplatesClone); // All categories for questionnaire
                for (let c = 0; c < defaultQuestionnaireTemplate.questionCategoryTemplates.length; c++) {

                    let tempCategoryTemplate = defaultQuestionnaireTemplate.questionCategoryTemplates[c];

                    let categoryGroupKeys = [...tempCategoryTemplate.groupKeys, ...tempCategoryTemplate.showGroupKeys];
                    tempCategoryTemplate.groupTemplates = [];

                    for (let g = 0; g < (categoryGroupKeys || []).length; g++) {
                        if (groupTemplatesClone[categoryGroupKeys[g]]) {

                            groupTemplatesClone[categoryGroupKeys[g]].visible = !tempCategoryTemplate.showGroupKeys.includes(categoryGroupKeys[g]);
                            tempCategoryTemplate.groupTemplates.push(groupTemplatesClone[categoryGroupKeys[g]]);

                            let groupQuestionKeys = groupTemplatesClone[categoryGroupKeys[g]].questionKeys;
                            tempCategoryTemplate.groupTemplates[g].questionTemplates = [];
                            for (let q = 0; q < (groupQuestionKeys || []).length; q++) {
                                if (questionTemplatesClone[groupQuestionKeys[q]]) {
                                    tempCategoryTemplate.groupTemplates[g].questionTemplates.push(questionTemplatesClone[groupQuestionKeys[q]]);
                                }
                            }
                        }
                    }



                }

            } else if (props.templateType === TEMPLATE.TYPE.CATEGORY) {
                if (templateClone.id === undefined) {
                    templateClone.id = 11;
                    templateClone.templateKey = "categoryKey11";
                }

                defaultQuestionnaireTemplate.questionCategoryTemplates.push(templateClone);

                let tempCategory = defaultQuestionnaireTemplate.questionCategoryTemplates[0];

                let categoryGroupKeys = [...tempCategory.groupKeys, ...tempCategory.showGroupKeys];

                tempCategory.groupTemplates = [];
                for (let g = 0; g < (categoryGroupKeys || []).length; g++) {
                    if (groupTemplatesClone[categoryGroupKeys[g]]) {

                        groupTemplatesClone[categoryGroupKeys[g]].visible = !tempCategory.showGroupKeys.includes(categoryGroupKeys[g]);
                        tempCategory.groupTemplates.push(groupTemplatesClone[categoryGroupKeys[g]]);

                        let groupQuestionKeys = groupTemplatesClone[categoryGroupKeys[g]].questionKeys;
                        tempCategory.groupTemplates[g].questionTemplates = [];
                        for (let q = 0; q < (groupQuestionKeys || []).length; q++) {
                            if (questionTemplatesClone[groupQuestionKeys[q]]) {
                                tempCategory.groupTemplates[g].questionTemplates.push(questionTemplatesClone[groupQuestionKeys[q]]);
                            }
                        }
                    }
                }


                // Somehow will get show groups for group templates and question templates
                // will need to add them to the dummy category template
                // templateClone.showGroupKeys QQQQQ


            } else if (props.templateType === TEMPLATE.TYPE.GROUP) {

                //debugger; // SSSSS Show Group Testing

                if (templateClone.id === undefined) {
                    templateClone.id = 111;
                    templateClone.templateKey = "groupKey111";
                }

                defaultQuestionnaireTemplate.questionCategoryTemplates.push(defaultCategoryTemplate);

                if (templateClone.questionGroupType === "LIKERT") {
                    let answers = templateClone.answerOptionTemplates || [];
                    for (let aa = 0; aa < answers.length; aa++) {
                        if (answers[aa].id === undefined) {
                            answers[aa].id = aa;
                            answers[aa].templateKey = "answerKey" + aa;
                        }
                    }
                }

                defaultQuestionnaireTemplate.questionCategoryTemplates[0].groupTemplates.push(templateClone); // Add the group

                // Check for any show groups
                if (templateClone.hasOwnProperty("showGroupKeys")) {

                    if (!templateClone.showGroupKeys) {
                        templateClone.showGroupKeys = []; // Somewhere under some conditions this becomes NULL so check and fix here for now
                    }

                    for (let i = 0; i < templateClone.showGroupKeys.length; i++) {

                        if (groupTemplates && groupTemplates.hasOwnProperty(templateClone.showGroupKeys[i])) {

                            let showGroupTemplate = portalShared.cloneObj(groupTemplates[templateClone.showGroupKeys[i]]);
                            showGroupTemplate.visible = false;
                            showGroupTemplate.questionTemplates = [];
                            for (let q = 0; q < showGroupTemplate.questionKeys.length; q++) {
                                showGroupTemplate.questionTemplates.push(questionTemplates[showGroupTemplate.questionKeys[q]]);
                            }
                            defaultQuestionnaireTemplate.questionCategoryTemplates[0].groupTemplates.push(showGroupTemplate); // Add the show group
                            defaultQuestionnaireTemplate.questionCategoryTemplates[0].showGroupKeys.push(templateClone.showGroupKeys[i]); // Add the show group key

                        }
                    }
                }

                let groupQuestionKeys = defaultQuestionnaireTemplate.questionCategoryTemplates[0].groupTemplates[0].questionKeys;
                defaultQuestionnaireTemplate.questionCategoryTemplates[0].groupTemplates[0].questionTemplates = [];
                for (let q = 0; q < (groupQuestionKeys || []).length; q++) {
                    if (questionTemplatesClone[groupQuestionKeys[q]]) {
                        defaultQuestionnaireTemplate.questionCategoryTemplates[0].groupTemplates[0].questionTemplates.push(questionTemplatesClone[groupQuestionKeys[q]]);
                    }
                }
            } else if (props.templateType === TEMPLATE.TYPE.QUESTION) {

                //debugger;

                defaultQuestionnaireTemplate.questionCategoryTemplates.push(defaultCategoryTemplate);
                defaultQuestionnaireTemplate.questionCategoryTemplates[0].groupTemplates.push(defaultGroupTemplate);

                // Check for any show groups
                if (templateClone.hasOwnProperty("showGroupKeys") && templateClone.showGroupKeys) {

                    for (let i = 0; i < templateClone.showGroupKeys.length; i++) {
                        let showGroupTemplate = portalShared.cloneObj(groupTemplates[templateClone.showGroupKeys[i]]);
                        showGroupTemplate.visible = false;
                        showGroupTemplate.questionTemplates = [];
                        for (let q = 0; q < showGroupTemplate.questionKeys.length; q++) {
                            showGroupTemplate.questionTemplates.push(questionTemplates[showGroupTemplate.questionKeys[q]]);
                        }
                        defaultQuestionnaireTemplate.questionCategoryTemplates[0].groupTemplates.push(showGroupTemplate); // Add the show group
                    }
                }

                if (templateClone.id === undefined) {
                    templateClone.id = 1111;
                    templateClone.templateKey = "questionKey1111";
                }

                templateClone.answerOptionTemplates = portalShared.cloneObj(props.answerOptionTemplates || []);

                for (let a = 0; a < (templateClone.answerOptionTemplates || []).length; a++) {
                    if (templateClone.answerOptionTemplates[a].id === undefined) {
                        templateClone.answerOptionTemplates[a].id = a;
                        templateClone.answerOptionTemplates[a].templateKey = "answerKey" + a;
                    }
                }

                defaultQuestionnaireTemplate.questionCategoryTemplates[0].groupTemplates[0].questionTemplates.push(templateClone);
            }
        }

        return adjustQuestionnaireTemplateGraph(defaultQuestionnaireTemplate);
    };

    // Render portal preview if all required templates are loaded
    const hasContent = state.questionnaireTemplateGraph !== null;

    if (!hasContent) {
        return null;  // first load only — no stale content to show
    }

    return (
        <div id={"ArgoPortalPreview-container-div1" + id} style={{display: hidePortalPreview ? "none" : "inline"}}>
            <div id="ArgoPortalPreview-container-div2" style={{overflow: "hidden"}}>
                <div style={{height: '20px'}}>&nbsp;</div>
                <div>
                    {(templateType !== TEMPLATE.TYPE.QUESTIONNAIRE_GRAPH) && (
                        <Divider id={"ArgoPortalPreview-divider-ArgoTemplateListDividerHook"}>
                            <Typography variant="subtitle1">Portal Preview</Typography>
                        </Divider>
                    )}
                    <div style={{overflowY: "auto"}}>
                        <div>&nbsp;</div>
                        <PortalContainer
                            portalPreview={true}
                            questionnaireTemplateGraph={state.questionnaireTemplateGraph}
                            portalCount={portalCount}
                            parent={props.calledBy}
                        />
                    </div>
                </div>
                <div style={{height: '20px'}}>&nbsp;</div>
            </div>
        </div>
    );
};

ArgoPortalPreviewHook.defaultProps = {
    createMode: false,
    hidePortalPreview: false,
};

ArgoPortalPreviewHook.propTypes = {
    calledBy: PropTypes.string,
    createMode: PropTypes.bool,
    templateType: PropTypes.string,
    template: PropTypes.object,
    answerOptionTemplates: PropTypes.array,
    hidePortalPreview: PropTypes.bool,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    templateGraph: PropTypes.object,
};

export default ArgoPortalPreviewHook;